import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Check, X, Calendar, Users, MapPin, Mountain } from 'lucide-react';

export const BhutanFamTripPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    // Applicant
    full_name: '',
    job_title: '',
    business_email: '',
    whatsapp_phone: '',
    country: '',
    city: '',
    // Company
    company_name: '',
    company_website: '',
    company_address: '',
    postal_code: '',
    business_type: '',
    // Professional Profile
    license_number: '',
    license_document: null,
    main_markets: '',
    main_destinations: '',
    annual_client_volume: '',
    // Motivation
    participation_reason: '',
    promotion_plan: '',
    // Media
    instagram: '',
    facebook: '',
    tiktok: '',
    youtube: '',
    website: '',
    other_media: '',
    // Declaration
    info_accurate: false,
    understands_contribution: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const businessTypes = [
    'Travel Agency',
    'Tour Operator',
    'Travel Advisor',
    'DMC',
    'Travel Influencer / Creator',
    'Travel Media',
    'Destination Specialist',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.info_accurate || !formData.understands_contribution) {
      toast({
        title: "Declaration Required",
        description: "Please check both declaration boxes to proceed.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'license_document' && formData[key]) {
          submitData.append(key, formData[key]);
        } else if (key !== 'license_document') {
          submitData.append(key, formData[key]);
        }
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/famtrip/apply`, {
        method: 'POST',
        body: submitData
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Application Submitted!",
          description: data.message || "Thank you for applying. We'll review your application within 48 hours.",
        });
        
        // Reset form
        setFormData({
          full_name: '', job_title: '', business_email: '', whatsapp_phone: '',
          country: '', city: '', company_name: '', company_website: '',
          company_address: '', postal_code: '', business_type: '', license_number: '', license_document: null,
          main_markets: '', main_destinations: '', annual_client_volume: '',
          participation_reason: '', promotion_plan: '', instagram: '',
          facebook: '', tiktok: '', youtube: '', website: '', other_media: '',
          info_accurate: false, understands_contribution: false
        });
        
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
        
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error(data.detail || 'Application failed');
      }
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.message || "Please try again or contact us directly at famtrip@indotravelista.com",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)' }}>
      <Navigation />
      <Toaster />
      <WhatsAppButton />

      {/* Hero Section */}
      <section style={{
        minHeight: '80vh',
        backgroundImage: 'url(https://images.unsplash.com/photo-1545030856-612d5feaf990?q=85&w=2000)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(7, 13, 30, 0.7), rgba(7, 13, 30, 0.85))'
        }} />
        
        <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '0 24px' }}>
          <p style={{
            color: '#FBBF24',
            fontSize: '14px',
            fontWeight: '600',
            letterSpacing: '2px',
            marginBottom: '16px',
            textTransform: 'uppercase'
          }}>
            B2B Famtrip • Bhutan
          </p>
          
          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 56px)',
            fontWeight: '300',
            lineHeight: '1.2',
            color: 'var(--text-primary)',
            marginBottom: '24px',
            fontFamily: 'Crimson Text, serif'
          }}>
            Druk Wangyel Festival<br />Famtrip
          </h1>
          
          <p style={{
            fontSize: '20px',
            color: 'var(--text-secondary)',
            marginBottom: '32px',
            maxWidth: '700px',
            margin: '0 auto 32px',
            lineHeight: '1.6'
          }}>
            A professional familiarization journey centered around Bhutan Druk Wangyel Festival, combining cultural experiences, destination discovery and first-hand travel knowledge.
          </p>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '32px',
            marginBottom: '32px',
            fontSize: '18px',
            color: 'var(--text-secondary)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={20} style={{ color: '#FBBF24' }} />
              <span>11–17 December 2026</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Mountain size={20} style={{ color: '#FBBF24' }} />
              <span>7 Days / 6 Nights</span>
            </div>
          </div>
          
          <a
            href="#apply"
            style={{
              display: 'inline-block',
              padding: '16px 48px',
              background: '#FBBF24',
              color: '#070D1E',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '4px',
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#D4AF37'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#FBBF24'}
          >
            APPLY FOR FAMTRIP
          </a>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{
            fontSize: '40px',
            fontWeight: '300',
            textAlign: 'center',
            color: 'var(--text-primary)',
            marginBottom: '16px',
            fontFamily: 'Crimson Text, serif'
          }}>
            Why Join This FAM Trip
          </h2>
          
          <p style={{
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            margin: '0 auto 48px',
            lineHeight: '1.8'
          }}>
            Experience Bhutan firsthand and gain the knowledge, insights, and connections to confidently promote and sell this extraordinary destination to your clients.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '32px' }}>
            {[
              { title: 'Destination Knowledge', desc: 'First-hand experience of Bhutan culture, festivals, and landscapes' },
              { title: 'Product Development', desc: 'Insights to create authentic Bhutan itineraries for your clients' },
              { title: 'Professional Network', desc: 'Connect with DMC partners and fellow travel professionals' },
              { title: 'Sales Advantage', desc: 'Sell with confidence from personal experience and visual content' }
            ].map((item, i) => (
              <div key={i} style={{
                background: 'var(--bg-primary)',
                padding: '32px',
                borderLeft: '4px solid #FBBF24',
                borderRadius: '4px'
              }}>
                <h3 style={{ fontSize: '20px', color: 'var(--text-primary)', marginBottom: '12px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* About the Festival Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{
            fontSize: '40px',
            fontWeight: '300',
            textAlign: 'center',
            color: 'var(--text-primary)',
            marginBottom: '32px',
            fontFamily: 'Crimson Text, serif'
          }}>
            About the Festival
          </h2>
          
          <p style={{
            color: 'var(--text-secondary)',
            lineHeight: '1.8',
            fontSize: '17px',
            textAlign: 'center'
          }}>
            The Druk Wangyel Festival is an annual celebration held at Dochula Pass on December 13. Set beside the 108 Druk Wangyel Chortens, the open-air mountain setting provides a distinctive backdrop for masked dances, traditional performances and ceremonial cultural programs. The festival reflects Bhutanese national identity, spiritual heritage, monarchy, armed forces and national history.
          </p>
        </div>
      </section>

      {/* Who Can Apply Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{
            fontSize: '40px',
            fontWeight: '300',
            textAlign: 'center',
            color: 'var(--text-primary)',
            marginBottom: '16px',
            fontFamily: 'Crimson Text, serif'
          }}>
            Who Can Apply
          </h2>
          
          <p style={{
            textAlign: 'center',
            color: 'var(--text-secondary)',
            marginBottom: '48px'
          }}>
            This B2B FAM Trip is designed exclusively for travel industry professionals:
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '24px'
          }}>
            {[
              'Travel Agents',
              'Tour Operators',
              'Travel Advisors',
              'DMCs',
              'Travel Influencers / Creators',
              'Travel Media',
              'Destination Specialists'
            ].map((type, i) => (
              <div key={i} style={{
                background: 'rgba(139, 0, 0, 0.1)',
                padding: '20px',
                borderRadius: '4px',
                textAlign: 'center',
                border: '1px solid rgba(139, 0, 0, 0.3)'
              }}>
                <Check size={24} style={{ color: '#FBBF24', marginBottom: '12px' }} />
                <p style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bhutan Experience Section */}
      <section className="section-padding" style={{
        background: 'var(--bg-secondary)'
      }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{
            fontSize: '40px',
            fontWeight: '300',
            textAlign: 'center',
            color: 'var(--text-primary)',
            marginBottom: '48px',
            fontFamily: 'Crimson Text, serif'
          }}>
            The Bhutan Experience
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px' }}>
            {[
              { title: 'Druk Wangyel Festival', desc: 'Experience this unique annual festival honoring Bhutan monarchy and victory, featuring masked dances and cultural performances at Dochula Pass.' },
              { title: 'Dochula Pass & 108 Chortens', desc: 'Panoramic Himalayan views with the iconic 108 memorial chortens, a stunning blend of spirituality and mountain majesty.' },
              { title: 'Tigers Nest Monastery', desc: 'Hike to Bhutan most iconic clifftop monastery, Taktsang Palphug, perched 900m above Paro Valley.' },
              { title: 'Dzongs & Monasteries', desc: 'Visit Punakha Dzong, Tashichho Dzong, Kyichu Lhakhang, and Gangtey Monastery—architectural masterpieces of Bhutanese heritage.' },
              { title: 'Phobjikha Valley', desc: 'Discover the glacial valley, home to endangered Black-Necked Cranes, and experience serene natural beauty.' },
              { title: 'Bhutanese Culture', desc: 'Immerse yourself in traditional hospitality, cuisine, arts, crafts, and the philosophy of Gross National Happiness.' }
            ].map((exp, i) => (
              <div key={i} style={{
                background: 'var(--bg-primary)',
                padding: '32px',
                borderRadius: '4px',
                border: '1px solid rgba(251, 191, 36, 0.2)'
              }}>
                <h3 style={{ fontSize: '22px', color: '#FBBF24', marginBottom: '16px' }}>
                  {exp.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  {exp.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Itinerary Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{
            fontSize: '40px',
            fontWeight: '300',
            textAlign: 'center',
            color: 'var(--text-primary)',
            marginBottom: '16px',
            fontFamily: 'Crimson Text, serif'
          }}>
            7-Day Itinerary
          </h2>
          
          <p style={{
            textAlign: 'center',
            color: 'var(--text-secondary)',
            marginBottom: '48px'
          }}>
            11–17 December 2026
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {[
              {
                day: 1, date: '11 December', title: 'Paro → Thimphu',
                desc: 'Arrival, Tachog viewpoint, Memorial Chorten', overnight: 'Thimphu'
              },
              {
                day: 2, date: '12 December', title: 'Thimphu',
                desc: 'Buddha Dordenma, traditional arts/crafts, Tashichho Dzong, festival briefing', overnight: 'Thimphu'
              },
              {
                day: 3, date: '13 December', title: 'Thimphu → Dochula → Punakha',
                desc: 'Druk Wangyel Festival, 108 chortens, mountain views', overnight: 'Punakha'
              },
              {
                day: 4, date: '14 December', title: 'Punakha → Phobjikha',
                desc: 'Punakha Dzong, suspension bridge, scenic drive', overnight: 'Phobjikha'
              },
              {
                day: 5, date: '15 December', title: 'Phobjikha → Paro',
                desc: 'Gangtey Monastery, valley walk, Black-Necked Crane Information Centre', overnight: 'Paro'
              },
              {
                day: 6, date: '16 December', title: 'Paro',
                desc: 'Tiger\'s Nest, Kyichu Lhakhang, farmhouse option', overnight: 'Paro'
              },
              {
                day: 7, date: '17 December', title: 'Paro → Departure',
                desc: 'Airport transfer and departure', overnight: ''
              }
            ].map((day, i) => (
              <div key={i} style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '4px',
                borderLeft: '4px solid #FBBF24'
              }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <p style={{ color: '#FBBF24', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                      DAY {day.day} — {day.date}
                    </p>
                    <h3 style={{ fontSize: '22px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                      {day.title}
                    </h3>
                  </div>
                  {day.overnight && (
                    <span style={{
                      background: 'rgba(139, 0, 0, 0.2)',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      color: '#FBBF24'
                    }}>
                      Overnight {day.overnight}
                    </span>
                  )}
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  {day.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Opportunity Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <h2 style={{
            fontSize: '40px',
            fontWeight: '300',
            color: 'var(--text-primary)',
            marginBottom: '24px',
            fontFamily: 'Crimson Text, serif'
          }}>
            B2B Professional Opportunity
          </h2>
          
          <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '32px', fontSize: '18px' }}>
            This FAM Trip provides travel professionals with first-hand experience to confidently develop and sell Bhutan travel products to their clients. Experience the destination, understand the logistics, capture content, and build relationships with DMC partners.
          </p>
          
          <div style={{
            background: 'var(--bg-secondary)',
            padding: '32px',
            borderRadius: '8px',
            border: '1px solid rgba(251, 191, 36, 0.2)'
          }}>
            <p style={{ color: 'var(--text-primary)', lineHeight: '1.8' }}>
              After the FAM Trip, you will be equipped to create authentic Bhutan itineraries, answer client questions with authority, and promote Bhutan with confidence backed by personal experience. This is an investment in your professional knowledge and product portfolio.
            </p>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{
            fontSize: '40px',
            fontWeight: '300',
            textAlign: 'center',
            color: 'var(--text-primary)',
            marginBottom: '24px',
            fontFamily: 'Crimson Text, serif'
          }}>
            Application Process
          </h2>
          
          <p style={{
            textAlign: 'center',
            color: 'var(--text-secondary)',
            marginBottom: '48px',
            fontSize: '16px'
          }}>
            Please allow up to 7 working days for the required approval process with the relevant Kingdom of Bhutan authority.
          </p>

          {/* Desktop: Horizontal Flow */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            gap: '16px', 
            marginBottom: '32px',
            flexWrap: 'nowrap',
            overflowX: 'auto'
          }}
          className="application-process-desktop">
            {[
              'Submit Application',
              'Application Review',
              'Approval with Relevant Kingdom of Bhutan Authority (up to 7 working days)',
              'Participant Confirmation & Payment Instructions',
              'FAM Trip'
            ].map((step, i, arr) => (
              <React.Fragment key={i}>
                <div style={{
                  background: 'var(--bg-primary)',
                  padding: '24px 20px',
                  borderRadius: '4px',
                  border: '2px solid #FBBF24',
                  minWidth: '160px',
                  flex: '1',
                  textAlign: 'center'
                }}>
                  <p style={{ fontSize: '14px', color: '#FBBF24', marginBottom: '8px', fontWeight: '600' }}>
                    STEP {i + 1}
                  </p>
                  <p style={{ color: 'var(--text-primary)', fontWeight: '500', fontSize: '13px', lineHeight: '1.4' }}>
                    {step}
                  </p>
                </div>
                {i < arr.length - 1 && (
                  <div style={{ color: '#FBBF24', fontSize: '28px', flexShrink: 0 }}>→</div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Mobile: Vertical Flow */}
          <div style={{ display: 'none' }} className="application-process-mobile">
            {[
              'Submit Application',
              'Application Review',
              'Approval with Relevant Kingdom of Bhutan Authority (up to 7 working days)',
              'Participant Confirmation & Payment Instructions',
              'FAM Trip'
            ].map((step, i, arr) => (
              <React.Fragment key={i}>
                <div style={{
                  background: 'var(--bg-primary)',
                  padding: '24px',
                  borderRadius: '4px',
                  border: '2px solid #FBBF24',
                  textAlign: 'center',
                  marginBottom: i < arr.length - 1 ? '16px' : 0
                }}>
                  <p style={{ fontSize: '14px', color: '#FBBF24', marginBottom: '8px', fontWeight: '600' }}>
                    STEP {i + 1}
                  </p>
                  <p style={{ color: 'var(--text-primary)', fontWeight: '500', fontSize: '14px', lineHeight: '1.4' }}>
                    {step}
                  </p>
                </div>
                {i < arr.length - 1 && (
                  <div style={{ color: '#FBBF24', fontSize: '28px', textAlign: 'center', margin: '8px 0' }}>↓</div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div style={{
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            padding: '24px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ color: 'var(--text-primary)', lineHeight: '1.8', marginBottom: '16px' }}>
              Application does not guarantee acceptance. Payment instructions are provided only after approval and confirmation.
            </p>
          </div>
        </div>

        <style>{`
          @media (max-width: 768px) {
            .application-process-desktop {
              display: none !important;
            }
            .application-process-mobile {
              display: flex !important;
              flex-direction: column;
            }
          }
        `}</style>
      </section>

      {/* Application Form */}
      <section id="apply" className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px' }}>
          <h2 style={{
            fontSize: '40px',
            fontWeight: '300',
            textAlign: 'center',
            color: 'var(--text-primary)',
            marginBottom: '16px',
            fontFamily: 'Crimson Text, serif'
          }}>
            Application Form
          </h2>
          
          <p style={{
            textAlign: 'center',
            color: 'var(--text-secondary)',
            marginBottom: '24px'
          }}>
            Druk Wangyel Festival Bhutan FAM Trip 2026
          </p>

          {/* Important Notices */}
          <div style={{
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '48px'
          }}>
            <h4 style={{
              color: '#FBBF24',
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '16px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Important Information
            </h4>
            <ul style={{
              color: 'var(--text-secondary)',
              lineHeight: '1.8',
              fontSize: '15px',
              paddingLeft: '20px',
              margin: 0
            }}>
              <li style={{ marginBottom: '12px' }}>
                <strong>Application Review:</strong> Your application will be reviewed within 48 hours of submission.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Approval Timeline:</strong> Approval with the relevant Kingdom of Bhutan authority may require up to 7 working days.
              </li>
              <li style={{ marginBottom: '12px' }}>
                <strong>Selection Process:</strong> Application does not guarantee acceptance. Final selection is based on professional qualifications and program suitability.
              </li>
              <li>
                <strong>Contribution Fee:</strong> The US$600 participation contribution is payable only if your application is selected and confirmed.
              </li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {/* Applicant Section */}
            <div>
              <h3 style={{ fontSize: '24px', color: '#FBBF24', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid rgba(251, 191, 36, 0.3)' }}>
                Applicant Information
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Job Title / Position *
                  </label>
                  <input
                    type="text"
                    name="job_title"
                    value={formData.job_title}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Business Email *
                  </label>
                  <input
                    type="email"
                    name="business_email"
                    value={formData.business_email}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    WhatsApp / Phone *
                  </label>
                  <input
                    type="text"
                    name="whatsapp_phone"
                    value={formData.whatsapp_phone}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Country *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Company Section */}
            <div>
              <h3 style={{ fontSize: '24px', color: '#FBBF24', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid rgba(251, 191, 36, 0.3)' }}>
                Company Information
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Company Website
                  </label>
                  <input
                    type="url"
                    name="company_website"
                    value={formData.company_website}
                    onChange={handleInputChange}
                    placeholder="https://"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Company Address *
                  </label>
                  <input
                    type="text"
                    name="company_address"
                    value={formData.company_address}
                    onChange={handleInputChange}
                    required
                    placeholder="Street address"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Postal / ZIP Code *
                  </label>
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>


                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Business Type *
                  </label>
                  <select
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  >
                    <option value="">Select business type</option>
                    {businessTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Professional Profile Section */}
            <div>
              <h3 style={{ fontSize: '24px', color: '#FBBF24', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid rgba(251, 191, 36, 0.3)' }}>
                Professional Profile
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                      IATA / Tourism License Number
                    </label>
                    <input
                      type="text"
                      name="license_number"
                      value={formData.license_number}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        borderRadius: '4px',
                        color: 'var(--text-primary)',
                        fontSize: '16px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                      Tourism / Business License Document
                    </label>
                    <input
                      type="file"
                      name="license_document"
                      onChange={handleInputChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        borderRadius: '4px',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      PDF, JPG, or PNG (max 5MB)
                    </p>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Main Markets Served
                  </label>
                  <input
                    type="text"
                    name="main_markets"
                    value={formData.main_markets}
                    onChange={handleInputChange}
                    placeholder="e.g., North America, Europe, Asia"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Main Destinations Sold
                  </label>
                  <input
                    type="text"
                    name="main_destinations"
                    value={formData.main_destinations}
                    onChange={handleInputChange}
                    placeholder="e.g., Southeast Asia, India, Nepal"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Estimated Annual Client Volume
                  </label>
                  <select
                    name="annual_client_volume"
                    value={formData.annual_client_volume}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  >
                    <option value="">Select range</option>
                    <option value="1-50">1-50 clients</option>
                    <option value="51-100">51-100 clients</option>
                    <option value="101-500">101-500 clients</option>
                    <option value="500+">500+ clients</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Motivation Section */}
            <div>
              <h3 style={{ fontSize: '24px', color: '#FBBF24', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid rgba(251, 191, 36, 0.3)' }}>
                Motivation
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Why would you like to participate in the Druk Wangyel Festival Bhutan FAM Trip? *
                  </label>
                  <textarea
                    name="participation_reason"
                    value={formData.participation_reason}
                    onChange={handleInputChange}
                    required
                    rows="5"
                    placeholder="Share your reasons for joining this FAM Trip..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    How do you plan to promote or sell Bhutan after the FAM Trip? *
                  </label>
                  <textarea
                    name="promotion_plan"
                    value={formData.promotion_plan}
                    onChange={handleInputChange}
                    required
                    rows="5"
                    placeholder="Describe your plans for promoting Bhutan to your clients/audience..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Media/Marketing Section */}
            <div>
              <h3 style={{ fontSize: '24px', color: '#FBBF24', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid rgba(251, 191, 36, 0.3)' }}>
                Media / Marketing (Optional)
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Instagram
                  </label>
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    placeholder="@username"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Facebook
                  </label>
                  <input
                    type="text"
                    name="facebook"
                    value={formData.facebook}
                    onChange={handleInputChange}
                    placeholder="Page or profile URL"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    TikTok
                  </label>
                  <input
                    type="text"
                    name="tiktok"
                    value={formData.tiktok}
                    onChange={handleInputChange}
                    placeholder="@username"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    YouTube
                  </label>
                  <input
                    type="text"
                    name="youtube"
                    value={formData.youtube}
                    onChange={handleInputChange}
                    placeholder="Channel URL"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: '500' }}>
                    Website or Other Media
                  </label>
                  <input
                    type="text"
                    name="other_media"
                    value={formData.other_media}
                    onChange={handleInputChange}
                    placeholder="Blog, podcast, or other media channels"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Declaration Section */}
            <div>
              <h3 style={{ fontSize: '24px', color: '#FBBF24', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid rgba(251, 191, 36, 0.3)' }}>
                Declaration *
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="info_accurate"
                    checked={formData.info_accurate}
                    onChange={handleInputChange}
                    required
                    style={{
                      marginTop: '4px',
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                    I confirm that the information provided in this application is accurate and that I understand this is a B2B Famtrip for travel industry professionals.
                  </span>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="understands_contribution"
                    checked={formData.understands_contribution}
                    onChange={handleInputChange}
                    required
                    style={{
                      marginTop: '4px',
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                    I understand that if my application is selected and I am confirmed as a participant, I am willing to pay the <strong>US$600 Famtrip contribution</strong>. International airfare / flight tickets are not included.
                  </span>
                </label>

                <div style={{
                  background: 'rgba(251, 191, 36, 0.1)',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  padding: '16px',
                  borderRadius: '4px',
                  marginTop: '8px'
                }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>
                    <strong>Note:</strong> Payment is only required if your application is approved and you are confirmed as a participant. The approval process includes coordination with the relevant Kingdom of Bhutan authority and may take up to 7 working days.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div style={{ textAlign: 'center', paddingTop: '24px' }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '16px 64px',
                  background: isSubmitting ? '#666' : '#FBBF24',
                  color: '#070D1E',
                  fontSize: '18px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) e.currentTarget.style.background = '#D4AF37';
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) e.currentTarget.style.background = '#FBBF24';
                }}
              >
                {isSubmitting ? 'SUBMITTING...' : 'SUBMIT APPLICATION'}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};
